import React, { useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import IMAGES from "@/public/assets/img";
import Head from "next/head";
import { WEB_URL } from "@/config/endpoints";
import exportFactoringInterest from "@/config/export_factoring_interest_config";
import ContactFormStep1 from "@/component/shared/contactForm/contactFormStep1";
import ContactFormStep2 from "@/component/shared/contactForm/contactFormStep2";
import { FormProvider } from "@/component/shared/contactForm/formContext";
import useIsMobile from "@/shared/useIsMobile";
import AccordianFaq from "@/component/shared/accordianfaq";
import { generateFaqSchema } from "@/component/shared/faqSchema";
import TrustedSection from "@/component/dealerFinancing/trustedSection";
import ExportMoney from "@/component/exportFactoringInterest/exportMoney";
import CredlixVsOthers from "@/component/exportFactoringInterest/credlixVsOthers";
import SavingsAndProcess from "@/component/exportFactoringInterest/savingsAndProcess";
import WhoShouldApply from "@/component/exportFactoringInterest/whoShouldApply";
import ExportGains from "@/component/exportFactoringInterest/exportGains";
import ExportCta from "@/component/exportFactoringInterest/exportCta";

const ExportFactoringInterest = () => {
  const router = useRouter();
  const [showNextStep, setShowNextStep] = useState(false);
  const faqSchema = generateFaqSchema(exportFactoringInterest.Faq);
  const topSectionRef = useRef(null);
  const isMobile = useIsMobile();

  const scrollToTopSection = () => {
    if (topSectionRef.current) {
      const headerOffset = 110;
      const elementPosition = topSectionRef.current.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const openMeetingModal = () => {
    if (typeof window !== "undefined") {
      const meetingBtn = document.querySelector("button.fixed.bottom-3") || 
        Array.from(document.getElementsByTagName("button")).find(
          (btn) => btn.textContent.trim() === "Book a meeting"
        );
      if (meetingBtn) {
        meetingBtn.click();
      }
    }
  };

  const showSteps = () => {
    setShowNextStep(true);
  };

  const showFirstStep = () => {
    setShowNextStep(false);
  };


  return (
    <main className="pt-[110px]">
      <Head>
        <title>{exportFactoringInterest.seoData.title}</title>
        <meta
          name="description"
          content={exportFactoringInterest.seoData.description}
        />
        {/* OpenGraph tags */}
        <meta property="og:title" content={exportFactoringInterest.seoData.title} />
        <meta
          property="og:description"
          content={exportFactoringInterest.seoData.description}
        />
        <meta property="og:image" content={IMAGES.HOMEIMG.bigLogoNew.src} />
        <meta property="og:url" content={WEB_URL + router.asPath} />
        <link rel="canonical" href={WEB_URL + router.asPath} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema),
          }}
        />
      </Head>
      <section
        ref={topSectionRef}
        className="relative bg-no-repeat sm:bg-center bg-cover bg-bottom pt-3 min-h-[510px] sm:min-h-fit flex items-center"
      >
        {!isMobile ? (
          <Image
            priority={true}
            src={IMAGES.exportInterest.exportInterestBg}
            width={1366}
            height={550}
            className="absolute inset-0 max-w-none w-full h-full"
            alt="Banner Desktop"
          />
        ) : (
          <Image
            priority={true}
            src={IMAGES.exportInterest.exportInterestBgMobile}
            width={767}
            height={451}
            className="absolute inset-0 max-w-none w-full h-full object-cover"
            alt="Banner Mobile"
          />
        )}
        <div className="container relative px-3 flex justify-between items-center sm:flex-col sm:pt-5 gap-8 sm:gap-0">
          <div className="w-[57%] sm:w-full">
            {exportFactoringInterest.badgeText && (
              <div className="border border-[#FFFFFF] bg-[#B1B1B14D] rounded-full px-4 py-1.5 mb-4 text-xs text-white w-fit">
                {exportFactoringInterest.badgeText}
              </div>
            )}
            <h1
              className="text-white text-heading5 sm:text-2xl font-medium"
              dangerouslySetInnerHTML={{ __html: exportFactoringInterest.bannerHeading }}
            />
            <div className="flex gap-2 mt-6">
              {exportFactoringInterest.features.map((item, index) => (
                <React.Fragment key={index}>
                  <div className="flex flex-col items-start">
                    <p
                      className={`text-white ${item.line1Bold ? "font-bold text-sm sm:text-xs" : "font-normal"}`}
                      dangerouslySetInnerHTML={{ __html: item.line1 }}
                    />
                    <p
                      className={`text-white ${item.line2Bold ? "font-bold text-sm sm:text-xs" : "font-normal"}`}
                      dangerouslySetInnerHTML={{ __html: item.line2 }}
                    />
                  </div>
                  {index < exportFactoringInterest.features.length - 1 && (
                    <div className="w-[1px] bg-[#FFFFFF33] self-stretch" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="w-[40%] sm:w-full sm:pt-2 flex justify-center z-10">
              <FormProvider>
               {!showNextStep ?
                 <ContactFormStep1 showStep1={showSteps} pageTitle="export-factoring-interest-subvention"/> :
                 <ContactFormStep2 showPrevStep={showFirstStep} formType="GetInTouchNew" pageTitle="export-factoring-interest-subvention"/>
               }
             </FormProvider>
          </div>
        </div>
      </section>
      <TrustedSection trustData={exportFactoringInterest.benefits} pageName="export-interst"/>
      <ExportMoney data={exportFactoringInterest.exportMoney} />

      <CredlixVsOthers data={exportFactoringInterest.credlixVsOthers} />
      <SavingsAndProcess
        savingsData={exportFactoringInterest.exportSavings}
        processData={exportFactoringInterest.exportProcess}
        onApplyNow={scrollToTopSection}
      />
      <WhoShouldApply data={exportFactoringInterest.whoShouldApply} />
      <ExportGains data={exportFactoringInterest.exportGains} />
      <section className="bg-white py-sp-100 sm:py-sp-big">
        <div className="container px-3 xm:px-2">
          <div>
            <h3 className="mb-6 text-darkBlack text-heading2 sm:text-2xl font-medium text-center">
              {exportFactoringInterest.FaqHead}
            </h3>
          </div>
          <div className="w-full text-left bg-white">
            <div className="mx-auto max-w-[1100px] sm:w-auto">
              <AccordianFaq faqData={exportFactoringInterest.Faq} />
            </div>
          </div>
        </div>
      </section>
      <ExportCta
        data={exportFactoringInterest.exportCta}
        onBookClick={openMeetingModal}
        onCheckClick={scrollToTopSection}
      />
    </main>
  );
};

export default ExportFactoringInterest;
